/**
 * Fase 8 — Markdown seguro para as respostas da IA. Sem HTML cru e sem `dangerouslySetInnerHTML`:
 * o texto vira elementos React (títulos, parágrafos, listas, negrito, itálico, código e links
 * https). Cobre o que o assistente produz; o resto fica como texto.
 */
import { Fragment, type ReactNode } from "react";

function inline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  // negrito, itálico, código e links [texto](https://...). Como no CommonMark, `_` dentro de
  // palavra (ex.: `act_503`) não abre nem fecha itálico.
  const re =
    /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\(https?:\/\/[^\s)]+\)|(?<![\p{L}\p{N}_])_(?:[^_\n]|(?<=[\p{L}\p{N}])_(?=[\p{L}\p{N}]))+_(?![\p{L}\p{N}_]))/gu;
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(re)) {
    const idx = m.index;
    if (idx > last) out.push(text.slice(last, idx));
    const token = m[0];
    const key = `${keyPrefix}-${i++}`;
    if (token.startsWith("**")) out.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    else if (token.startsWith("`")) out.push(<code key={key} className="rounded bg-muted px-1 py-0.5 text-[0.85em]">{token.slice(1, -1)}</code>);
    else if (token.startsWith("[")) {
      const close = token.indexOf("](");
      const label = token.slice(1, close);
      const href = token.slice(close + 2, -1);
      out.push(
        <a key={key} href={href} target="_blank" rel="noopener noreferrer" className="underline">
          {label}
        </a>,
      );
    } else out.push(<em key={key}>{token.slice(1, -1)}</em>);
    last = idx + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

type Block =
  | { type: "h"; level: number; text: string }
  | { type: "p"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] };

function parse(markdown: string): Block[] {
  const blocks: Block[] = [];
  let para: string[] = [];
  let list: { type: "ul" | "ol"; items: string[] } | null = null;
  const flushPara = () => {
    if (para.length > 0) blocks.push({ type: "p", text: para.join(" ") });
    para = [];
  };
  const flushList = () => {
    if (list) blocks.push(list);
    list = null;
  };
  for (const raw of markdown.split("\n")) {
    const line = raw.trimEnd();
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    const ul = /^\s*[-*•]\s+(.*)$/.exec(line);
    const ol = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (h) {
      flushPara();
      flushList();
      blocks.push({ type: "h", level: h[1]?.length ?? 2, text: h[2] ?? "" });
    } else if (ul) {
      flushPara();
      if (list?.type !== "ul") {
        flushList();
        list = { type: "ul", items: [] };
      }
      list.items.push(ul[1] ?? "");
    } else if (ol) {
      flushPara();
      if (list?.type !== "ol") {
        flushList();
        list = { type: "ol", items: [] };
      }
      list.items.push(ol[1] ?? "");
    } else if (line.trim() === "") {
      flushPara();
      flushList();
    } else {
      flushList();
      para.push(line.trim());
    }
  }
  flushPara();
  flushList();
  return blocks;
}

export function Markdown({ text, className }: { text: string; className?: string }) {
  const blocks = parse(text);
  return (
    <div className={className ?? "space-y-2 text-sm leading-relaxed"}>
      {blocks.map((b, i) => {
        const key = `b${i}`;
        switch (b.type) {
          case "h":
            return b.level <= 2 ? (
              <h3 key={key} className="mt-3 text-base font-semibold first:mt-0">
                {inline(b.text, key)}
              </h3>
            ) : (
              <h4 key={key} className="mt-2 text-sm font-semibold">
                {inline(b.text, key)}
              </h4>
            );
          case "ul":
            return (
              <ul key={key} className="list-disc space-y-1 pl-5">
                {b.items.map((it, j) => (
                  <li key={`${key}-${j}`}>{inline(it, `${key}-${j}`)}</li>
                ))}
              </ul>
            );
          case "ol":
            return (
              <ol key={key} className="list-decimal space-y-1 pl-5">
                {b.items.map((it, j) => (
                  <li key={`${key}-${j}`}>{inline(it, `${key}-${j}`)}</li>
                ))}
              </ol>
            );
          default:
            return (
              <p key={key}>
                {inline(b.text, key).map((n, j) => (
                  <Fragment key={`${key}-${j}`}>{n}</Fragment>
                ))}
              </p>
            );
        }
      })}
    </div>
  );
}
